/** Path routing: `/` is the home page, `/e/<id>` a saved design and `/e/new` an empty editor. */

export type Route = { name: 'home' } | { name: 'editor'; id: string | null; sample: string | null };

export function parse(loc: { pathname: string; search: string } = location): Route {
  const m = /^\/e\/([\w-]+)\/?$/.exec(loc.pathname);
  if (!m) return { name: 'home' };
  const query = new URLSearchParams(loc.search);
  return { name: 'editor', id: m[1] === 'new' ? null : m[1], sample: query.get('sample') };
}

export function editorPath(id: string | null, sample?: string | null): string {
  const base = `/e/${id ?? 'new'}`;
  return sample ? `${base}?sample=${encodeURIComponent(sample)}` : base;
}

const listeners = new Set<() => void>();

/** Navigates within the app without a reload. */
export function go(path: string, { replace = false }: { replace?: boolean } = {}): void {
  if (location.pathname + location.search === path) return;
  history[replace ? 'replaceState' : 'pushState'](null, '', path);
  for (const fn of listeners) fn();
}

/** Rewrites the address without routing, for when the editor learns its design id. */
export function rewrite(path: string): void {
  if (location.pathname + location.search === path) return;
  history.replaceState(null, '', path);
}

/** Calls `fn` now and on every navigation; in-app links (`<a href="/…">`) route without a reload. */
export function onRoute(fn: () => void): () => void {
  listeners.add(fn);
  const onClick = (e: MouseEvent) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = (e.target as Element | null)?.closest?.('a[href^="/"]');
    if (!(a instanceof HTMLAnchorElement) || a.target === '_blank' || a.hasAttribute('download')) return;
    e.preventDefault();
    go(a.getAttribute('href') ?? '/');
  };
  window.addEventListener('popstate', fn);
  document.addEventListener('click', onClick);
  fn();
  return () => {
    listeners.delete(fn);
    window.removeEventListener('popstate', fn);
    document.removeEventListener('click', onClick);
  };
}
